'use client';

import { AxiosError } from 'axios';
// import { ZodError } from 'zod';
import { toastUtils } from '@/utils/toast';
import debounce from 'lodash-es/debounce';
import { BaseError } from 'viem';
import { useTranslations } from 'next-intl';
import { HttpResponseError } from '@/libs/http';
import { authSignout } from '@/features/auth/services/api-auth';
import { isInApp, setFreshToken } from '@/libs/comeAppSDK';
import { useWalletDisconnect } from '@/hooks/use-wallet-disconnect';
import { useSetAtom, useAtom } from 'jotai';
import { setFlagAtom } from '@/store/session';
import { userAtom } from '@/store';
import { thinkingLogout } from '@/utils/track';
import { findLocale } from '@/libs/find-locale';

const debouncedUnauthorizedToast = debounce((msg: string) => {
  toastUtils.error(msg, { duration: 2700, id: 'unauthorized-toast' });
}, 500, { leading: true });

// 轮询请求错误限频配置：同一接口 60s 内只 toast 一次，且至少第 3 次错误才 toast
const POLLING_ERROR_WINDOW_MS = 60 * 1000;
const POLLING_ERROR_MIN_COUNT = 3;

type PollingErrorRecord = {
  firstErrorTime: number;
  lastToastTime?: number;
  errorCount: number;
};

// 轮询错误限频的 key 维度
// 之前是「method + url」，会导致每个接口单独计数，实际体验是所有轮询类请求都不想频繁打扰用户
// 因此这里改为「全局轮询 key」，只要是标记为轮询的请求，都共用同一份错误计数和限频逻辑
const pollingErrorState = new Map<string, PollingErrorRecord>();

const getPollingKeyFromError = (error: Error): string | null => {
  let config: any;

  if (error instanceof HttpResponseError) {
    config = error.response?.config;
  } else if (error instanceof AxiosError) {
    config = error.config;
  }

  if (!config) {
    return null;
  }

  // 通过请求配置上的 isPollingRequest 标记识别轮询请求
  if (!(config as any).isPollingRequest) {
    return null;
  }

  // 不再按单个 URL 维度做区分，统一使用一个全局 key
  // 如后续需要更细粒度的控制，可以在请求上增加自定义字段（例如 pollingGroupKey）并在此读取
  return 'POLLING_REQUEST_GLOBAL_KEY';
};

const toastIgnoreUrlFragments = [
  '/message/notice',
  '/wallet/v1/balance_list',
];

const shouldSkipToast = (url?: string) => {
  if (!url) {
    return false;
  }
  return toastIgnoreUrlFragments.some(fragment => url.includes(fragment));
};

const getLogoutCallbackUrl = () => {
  // 确保在客户端环境
  if (typeof window === 'undefined') {
    return '/';
  }

  const currentLocale = findLocale(window.location.pathname);
  // 将完整的 locale (如 'en-US') 转换为前缀 (如 'en')
  const localePrefix = currentLocale.split('-')[0];
  if (isInApp()) {
    return `/login`;
  }
  return `/${localePrefix}`;
};

export const useHandleQueryError = () => {
  const t = useTranslations('common');
  const { triggerDisconnect } = useWalletDisconnect();
  const setSessionFlag = useSetAtom(setFlagAtom);
  const [, setUser] = useAtom(userAtom);

  return (error: Error) => {
    // 1. 处理 wagmi/viem 的 BaseError 并触发钱包断连
    if (
      error instanceof BaseError ||
      (typeof (error as any)?.version === 'string' && (error as any).version.includes('wagmi'))
    ) {
      // 触发钱包断连
      triggerDisconnect?.();
      return;
    }

    let message = t('errors.networkError');
    let status: number | undefined;
    let requestUrl: string | undefined;

    // 2. 处理由 http.ts 抛出的自定义业务错误
    if (error instanceof HttpResponseError) {
      message = error.message; // 直接使用后端返回的错误信息
      status = error.response.status;
      requestUrl = error.response.config?.url;
    } else if (error instanceof AxiosError) {
      // 3. 处理 Axios 自身的网络错误
      status = error.response?.status;
      requestUrl = error.config?.url;
      if (error.code === 'ECONNABORTED') {
        message = t('errors.requestTimeout');
      }
    }

    // 4. 根据 HTTP 状态码处理通用场景 (如 401 跳转)
    if (status === 401) {

      message = t('errors.unauthorized');
      if (!shouldSkipToast(requestUrl)) {
        debouncedUnauthorizedToast(message);
      }
      // token失效，重置user
      setUser({ name: '', accessToken: null });
      if (typeof window !== 'undefined') {
        // app内清空refreshToken
        if (isInApp()) {
          setFreshToken('');
        }
        // 触发钱包断连
        triggerDisconnect?.();
        // 数数埋点退出登录
        thinkingLogout();
        window.localStorage.removeItem('accessToken');
        window.sessionStorage.removeItem('preloader-video-played');
        setSessionFlag?.('logout');
        // 调用第三方登出接口
        authSignout({ params: {} }).catch(err => {
          console.warn('Third party logout failed:', err);
        });
        // 页面跳转
        const callbackUrl = getLogoutCallbackUrl();
        window.location.href = callbackUrl;
      }
      return; // 401 不再弹出普通错误提示
    }

    /*
    if (status === 403) {
      message = t('forbidden');
    }
    
    if (error instanceof ZodError) {
      message = t('validationError');
    } */

    // 5. 轮询请求的错误：60s 内只 toast 一次，且从第 3 次错误开始才 toast
    const pollingKey = getPollingKeyFromError(error);
    if (pollingKey) {
      const now = Date.now();
      const prev = pollingErrorState.get(pollingKey);

      // 窗口过期或首次记录，重置计数
      const isWindowExpired = prev && now - prev.firstErrorTime > POLLING_ERROR_WINDOW_MS;
      if (!prev || isWindowExpired) {
        // 窗口过期时，清空 lastToastTime，让新的窗口可以重新触发 toast
        const newRecord: PollingErrorRecord = {
          firstErrorTime: now,
          errorCount: 1,
          lastToastTime: isWindowExpired ? undefined : prev?.lastToastTime,
        };
        pollingErrorState.set(pollingKey, newRecord);

        // 首次记录时，errorCount 只有 1，不满足 >= 3 的条件，直接返回
        // 窗口过期重置后，也需要重新累计到 3 次才 toast
        return;
      }

      const nextCount = prev.errorCount + 1;
      const canToastByCount = nextCount >= POLLING_ERROR_MIN_COUNT;
      const canToastByWindow =
        !prev.lastToastTime || now - prev.lastToastTime > POLLING_ERROR_WINDOW_MS;

      const shouldToast = canToastByCount && canToastByWindow;

      pollingErrorState.set(pollingKey, {
        firstErrorTime: prev.firstErrorTime,
        errorCount: nextCount,
        lastToastTime: shouldToast ? now : prev.lastToastTime,
      });

      if (shouldToast) {
        toastUtils.error(message, { duration: 2700, id: 'general-error' });
      }

      return;
    }

    // 6. 为其他所有错误显示一个 Toast 提示（带固定 key 防止长时间重复显示）
    toastUtils.error(message, { duration: 2700, id: 'general-error' });
  };
};